import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Users, BookOpen, ClipboardCheck, Award } from "lucide-react";

const STATUS = [{ v: "aktif", l: "Aktif" }, { v: "selesai", l: "Selesai" }, { v: "diarsipkan", l: "Diarsipkan" }];
const PROGRAMS = ["EFK", "EYL", "EFJ"];
const BOOKS = ["SK2", "BO1", "BO2", "BO3", "BO4", "BO5", "BO6", "Link 1", "Link 2", "Link 3"];
const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jum'at", "Sabtu"];
const TIMES = ["07:30-08:45", "09:00-10:15", "13:30-15:00", "14:00-15:30", "14:30-16:00"];
const EMPTY = { school_id: "", academic_year: "", semester: "I", name: "", class_in_school: "", book_level: "", teacher_id: "", day: "", time: "", period_start: "", period_end: "", status: "aktif", note: "" };

export default function MyClasses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCoord = user?.role === "coordinator";
  const isBM = user?.role === "branch_manager";
  const canWrite = !isCoord;

  const [classes, setClasses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [meta, setMeta] = useState({ academic_years: [], semesters: [], active_year: "", active_semester: "I" });
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState({ school_id: "all", semester: "all" });

  const load = () => {
    const params = {};
    if (filter.school_id !== "all") params.school_id = filter.school_id;
    if (filter.semester !== "all") params.semester = filter.semester;
    api.get("/classes", { params }).then((r) => setClasses(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/my/schools").then((r) => setSchools(r.data)).catch(() => {});
    api.get("/academic/meta").then((r) => setMeta(r.data)).catch(() => {});
    if (isBM) api.get("/teachers").then((r) => setTeachers(r.data.filter((t) => !t.is_staff))).catch(() => {});
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const openNew = () => { setForm({ ...EMPTY, academic_year: meta.active_year || "", semester: meta.active_semester || "I" }); setDialog("new"); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setDialog(c.id); };

  const save = async () => {
    if (!form.school_id || !form.name || !form.academic_year) { toast.error("Sekolah, Tahun Pelajaran, dan Nama Kelas wajib diisi"); return; }
    if (isBM && !form.teacher_id) { toast.error("Pilih guru pengampu"); return; }
    try {
      if (dialog === "new") await api.post("/classes", form);
      else await api.put(`/classes/${dialog}`, form);
      toast.success("Kelas tersimpan"); setDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Hapus kelas "${c.name}"? Data siswa juga tersembunyi dan dapat dipulihkan Branch Manager.`)) return;
    try { await api.delete(`/classes/${c.id}`); toast.success("Kelas dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="space-y-5" data-testid="classes-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">{isBM ? "Manajemen Kelas" : isCoord ? "Data Kelas" : "Kelas Saya"}</h2>
          <p className="text-sm text-muted-foreground">Kelas / teaching group ekstrakurikuler Bahasa Inggris</p>
        </div>
        {canWrite && (
          <Button onClick={openNew} className="rounded-full bg-gradient-to-r from-gtsorange to-gtsorange-light text-white gap-1" data-testid="add-class-btn">
            <Plus className="w-4 h-4" /> Tambah Kelas
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={filter.school_id} onValueChange={(v) => setFilter({ ...filter, school_id: v })}>
          <SelectTrigger className="rounded-xl w-56" data-testid="filter-school"><SelectValue placeholder="Semua Sekolah" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Sekolah</SelectItem>
            {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.semester} onValueChange={(v) => setFilter({ ...filter, semester: v })}>
          <SelectTrigger className="rounded-xl w-44" data-testid="filter-semester"><SelectValue placeholder="Semua Semester" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Semester</SelectItem>
            <SelectItem value="I">Semester I</SelectItem>
            <SelectItem value="II">Semester II</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {classes.length === 0 ? (
        <Card className="rounded-2xl border-dashed p-10 text-center text-muted-foreground" data-testid="classes-empty">
          Belum ada kelas. {canWrite && "Klik \"Tambah Kelas\" untuk membuat."}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map((c) => (
            <Card key={c.id} className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-5 bg-white flex flex-col" data-testid={`class-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-gtsnavy to-gtsnavy/80 flex items-center justify-center"><BookOpen className="w-5 h-5 text-white" /></div>
                <Badge className={`rounded-full ${c.status === "aktif" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{STATUS.find((s) => s.v === c.status)?.l || c.status}</Badge>
              </div>
              <p className="font-nunito font-bold text-gtsnavy mt-3">{c.name}</p>
              <p className="text-xs text-muted-foreground">{c.school_name}{c.class_in_school ? ` · ${c.class_in_school}` : ""}{c.book_level ? ` · ${c.book_level}` : ""}</p>
              <p className="text-xs text-muted-foreground mt-1">Guru: {c.teacher_name} · Sem {c.semester} · {c.academic_year}</p>
              <div className="flex items-center gap-1.5 mt-2 text-sm text-gtsnavy"><Users className="w-4 h-4 text-gtsorange" /> {c.student_count} siswa</div>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" onClick={() => navigate(`/classes/${c.id}/students`)} className="rounded-full bg-gtsnavy text-white gap-1" data-testid={`students-class-${c.id}`}><Users className="w-3.5 h-3.5" /> Siswa</Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/classes/${c.id}/scores`)} className="rounded-full gap-1" data-testid={`scores-class-${c.id}`}><ClipboardCheck className="w-3.5 h-3.5" /> Nilai</Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/classes/${c.id}/reports`)} className="rounded-full gap-1" data-testid={`reports-class-${c.id}`}><Award className="w-3.5 h-3.5" /> Rapor</Button>
                {canWrite && <Button size="sm" variant="outline" onClick={() => openEdit(c)} className="rounded-full" data-testid={`edit-class-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                {canWrite && <Button size="sm" variant="outline" onClick={() => remove(c)} className="rounded-full text-rose-600 border-rose-200" data-testid={`delete-class-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dialog === "new" ? "Tambah Kelas" : "Edit Kelas"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Sekolah *</Label>
              <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="class-school"><SelectValue placeholder="Pilih sekolah" /></SelectTrigger>
                <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {isBM && (
              <div>
                <Label>Guru Pengampu *</Label>
                <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="class-teacher"><SelectValue placeholder="Pilih guru" /></SelectTrigger>
                  <SelectContent>{teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tahun Pelajaran *</Label>
                <Select value={form.academic_year} onValueChange={(v) => setForm({ ...form, academic_year: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="class-year"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{(meta.academic_years || []).map((y) => <SelectItem key={y.year} value={y.year}>{y.year}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Semester *</Label>
                <Select value={form.semester} onValueChange={(v) => setForm({ ...form, semester: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="class-semester"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="I">Semester I</SelectItem><SelectItem value="II">Semester II</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Nama Kelas / Program *</Label>
              <Select value={form.name} onValueChange={(v) => setForm({ ...form, name: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="class-name"><SelectValue placeholder="Pilih program" /></SelectTrigger>
                <SelectContent>{PROGRAMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kelas di Sekolah</Label><Input value={form.class_in_school || ""} onChange={(e) => setForm({ ...form, class_in_school: e.target.value })} className="rounded-xl mt-1" placeholder="cth: 2A" data-testid="class-in-school" /></div>
              <div>
                <Label>Buku / Level</Label>
                <Select value={form.book_level || ""} onValueChange={(v) => setForm({ ...form, book_level: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="class-book"><SelectValue placeholder="Pilih buku" /></SelectTrigger>
                  <SelectContent>{BOOKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hari</Label>
                <Select value={form.day || ""} onValueChange={(v) => setForm({ ...form, day: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="class-day"><SelectValue placeholder="Pilih hari" /></SelectTrigger>
                  <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Jam</Label>
                <Select value={form.time || ""} onValueChange={(v) => setForm({ ...form, time: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="class-time"><SelectValue placeholder="Pilih jam" /></SelectTrigger>
                  <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Periode Mulai</Label><Input type="date" value={form.period_start || ""} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="rounded-xl mt-1" data-testid="class-start" /></div>
              <div><Label>Periode Selesai</Label><Input type="date" value={form.period_end || ""} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="rounded-xl mt-1" data-testid="class-end" /></div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="class-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Catatan</Label><Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-xl mt-1" data-testid="class-note" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-gtsnavy text-white" data-testid="save-class-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
